import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Typo, SwitchRow, Flex, Icon, theme } from '../../../../ui';
import FileUploader from '../../../media/uploaders/FileUploader';
import { setGuideSettings, selectGuideSettings } from '../../courseGuideState';

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepHeader = styled.div`
  margin-bottom: 8px;
`;

const SettingBlock = styled.div`
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.NEU_50};
`;

const SettingContent = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.NEU_200};
`;

const StepSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const settings = useSelector(selectGuideSettings);

  const updateSetting = useCallback(
    (key, value) => {
      dispatch(setGuideSettings({ [key]: value }));
    },
    [dispatch],
  );

  const handleDiplomaFileUpload = useCallback(
    (file) => {
      if (file) {
        dispatch(setGuideSettings({ diplomaFileId: file.id }));
      }
    },
    [dispatch],
  );

  return (
    <StepWrapper>
      <StepHeader>
        <Typo.ContentBigHeader>{t('courseGuide.settings.title')}</Typo.ContentBigHeader>
        <Typo.SmallText>{t('courseGuide.settings.subtitle')}</Typo.SmallText>
      </StepHeader>

      {/* Quiz */}
      <SettingBlock>
        <SwitchRow
          label={t('courseGuide.settings.enableQuiz')}
          checked={settings.quizEnabled}
          onChange={(e) => updateSetting('quizEnabled', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.SmallText>{t('courseGuide.settings.quizDescription')}</Typo.SmallText>
      </SettingBlock>

      {/* Diploma */}
      <SettingBlock>
        <SwitchRow
          label={t('courseGuide.settings.enableDiploma')}
          checked={settings.diplomaEnabled}
          onChange={(e) => updateSetting('diplomaEnabled', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.SmallText>{t('courseGuide.settings.diplomaDescription')}</Typo.SmallText>
        {settings.diplomaEnabled && (
          <SettingContent>
            <Typo.SmallText $marginBottom='12px'>{t('courseGuide.settings.uploadDiplomaTemplate')}</Typo.SmallText>
            {settings.diplomaFileId ? (
              <Flex align='center' gap='8'>
                <Icon.Published color={theme.colors.PRIMARY} />
                <Typo.SmallText>{t('courseGuide.settings.diplomaTemplateUploaded')}</Typo.SmallText>
              </Flex>
            ) : (
              <FileUploader
                id='guideDiplomaTemplate'
                saveAction={handleDiplomaFileUpload}
                fileType='other'
                label={t('settings.uploadTemplate')}
                iconName='Upload'
                variant='customized'
              />
            )}
          </SettingContent>
        )}
      </SettingBlock>

      {/* Gated Access */}
      <SettingBlock>
        <SwitchRow
          label={t('courseGuide.settings.enableGatedAccess')}
          checked={settings.gatedAccess}
          onChange={(e) => updateSetting('gatedAccess', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.SmallText>{t('courseGuide.settings.gatedAccessDescription')}</Typo.SmallText>
      </SettingBlock>

      {/* Delayed Access */}
      <SettingBlock>
        <SwitchRow
          label={t('courseGuide.settings.enableDelayedAccess')}
          checked={settings.delayedAccess}
          onChange={(e) => updateSetting('delayedAccess', e.target.checked)}
          enabledText={t('globals.enabled')}
          disabledText={t('globals.disabled')}
        />
        <Typo.SmallText>{t('courseGuide.settings.delayedAccessDescription')}</Typo.SmallText>
      </SettingBlock>
    </StepWrapper>
  );
};

export default StepSettings;
